import { EntityType } from '@ftr/contracts/api/permissions'
import {
  CreatePermissionOnGroup,
  getPermissionDescription,
  UserGroupPermission,
  UserGroupPermissionId,
} from '@ftr/contracts/api/user-group'
import { VocabularyTerms } from '@ftr/contracts/type/core/VocabularyTerms'
import { StrictUuid, Uuid } from '@ftr/contracts/type/shared'
import { MapUtils } from '@ftr/foundation'
import { CurrentStage, UserGroupPermissionStateMap } from './UserGroupPermissionStateMap'

/**
 * Create Group and Add Permissions pages are interdependent, so this
 * interface is used to exchange data between them
 */

export class UserGroupState {
  userGroupId: StrictUuid | undefined
  userGroupName: string = ''
  userGroupDescription: string = ''
  courtSystemId: Uuid = ''
  courtSystemName: string = ''

  userGroupPermissionStateMap: UserGroupPermissionStateMap

  constructor(restrictedPermissions = new Map<UserGroupPermissionId, Set<EntityType>>()) {
    this.userGroupPermissionStateMap = new UserGroupPermissionStateMap([], restrictedPermissions)
  }

  canAccessUnlinkedEntity(permission: UserGroupPermissionId, entityType: EntityType, stage: CurrentStage): boolean {
    return this.userGroupPermissionStateMap.canAccessUnlinkedEntity(permission, entityType, stage)
  }

  areRestrictionsMisconfigured(): boolean {
    return this.userGroupPermissionStateMap.areRestrictionsMisconfigured()
  }

  /**
   * All enabled permissions must have locations restricted.
   *
   * Departments are optional, as you can give someone access to any resource without a department
   */
  isPermissionConfigured(permissionId: UserGroupPermissionId): boolean {
    return this.userGroupPermissionStateMap.isPermissionConfigured(permissionId)
  }

  /**
   * Set enabled in both staged/committed at the same time so validation works correctly.
   */
  setEnabled(permission: UserGroupPermissionId, isEnabled: boolean): void {
    this.userGroupPermissionStateMap.setEnabled(permission, isEnabled, 'staged')
    this.userGroupPermissionStateMap.setEnabled(permission, isEnabled, 'committed')
  }

  addRestriction(permissionId: UserGroupPermissionId, entityType: EntityType, entityId: Uuid): void {
    this.userGroupPermissionStateMap.addRestriction(permissionId, entityType, entityId)
  }

  removeRestriction(permissionId: UserGroupPermissionId, entityType: EntityType, entityId: Uuid): void {
    this.userGroupPermissionStateMap.removeRestriction(permissionId, entityType, entityId)
  }

  removeAllRestrictions(permissionId: UserGroupPermissionId, entityType: EntityType): void {
    this.userGroupPermissionStateMap.removeAllRestrictions(permissionId, entityType)
  }

  setUnlinkedAccess(permissionId: UserGroupPermissionId, entityType: EntityType, selected: boolean): void {
    this.userGroupPermissionStateMap.setUnlinkedAccess(permissionId, entityType, selected)
  }

  resetRestrictions(): void {
    this.userGroupPermissionStateMap.resetRestrictions()
  }

  getCreatePermissionOnGroupModels(): CreatePermissionOnGroup[] {
    return this.userGroupPermissionStateMap.getCreatePermissionOnGroupModels()
  }

  isPermissionRestrictedBy(
    permissionId: UserGroupPermissionId,
    entityType: EntityType,
    entityId: Uuid,
    stage: CurrentStage,
  ): boolean {
    return this.userGroupPermissionStateMap.isPermissionRestrictedBy(permissionId, entityType, entityId, stage)
  }

  isEnabled(permissionId: UserGroupPermissionId): boolean {
    return this.userGroupPermissionStateMap.isPermissionEnabled(permissionId)
  }

  getRestrictionCountPerEntity(permissionId: UserGroupPermissionId, stage: CurrentStage): Map<EntityType, number> {
    return this.userGroupPermissionStateMap.getRestrictionCountPerEntity(permissionId, stage)
  }

  populateFromUserGroupPermissions(permissions: UserGroupPermission[]): UserGroupPermissionStateMap {
    const restrictions = this.userGroupPermissionStateMap.restrictedPermissions
    return (this.userGroupPermissionStateMap = new UserGroupPermissionStateMap(permissions, restrictions))
  }

  public getPermissionDescriptionForSentenceUse(permission: UserGroupPermissionId): string | undefined {
    return getPermissionDescription(permission)?.toLowerCase().replace(/\..*$/s, '')
  }

  /**
   * An array of counts and labels.
   *
   * Eg ["12 locations", "2 departments"]
   */
  getRestrictedEntityLabelAndCountArray(
    terms: VocabularyTerms,
    permission: UserGroupPermissionId,
    stage: CurrentStage = 'committed',
  ): string[] {
    const counts = this.userGroupPermissionStateMap.getRestrictionCountPerEntity(permission, stage)

    return MapUtils.reduce(
      counts,
      (acc, [entityType, count]) => {
        const text = this.getConcatenatedCountAndLabel(terms, entityType, count)
        return acc.concat([text])
      },
      [] as string[],
    )
  }

  getConcatenatedCountAndLabel(terms: VocabularyTerms, key: EntityType, count: number): string {
    const label = this.getVocabbedKey(terms, key, count).toLowerCase()
    return `${count} ${label}`
  }

  getVocabbedKey(terms: VocabularyTerms, key: EntityType, count: number): string {
    const keyToCheck = key === 'location' ? 'courtroom' : key
    if (Object.keys(terms).includes(keyToCheck)) {
      const term = terms[keyToCheck as keyof VocabularyTerms]
      return count > 1 || count === 0 ? term.plural : term.singular
    }

    return count > 1 || count === 0 ? keyToCheck + 's' : keyToCheck
  }

  getEntityIds(permissionId: UserGroupPermissionId, entityType: EntityType, stage: CurrentStage): Set<Uuid> {
    return this.userGroupPermissionStateMap.getEntityIds(permissionId, entityType, stage)
  }

  commitRestrictions(permissionId: UserGroupPermissionId): void {
    this.userGroupPermissionStateMap.propagateStagedRestrictions(permissionId)
    this.userGroupPermissionStateMap.commitRestrictions()
  }
}
